import { AsyncPipe, NgIf, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, filter, map, take } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { loadAllGeneralRoles } from '@main-application/administration/store/actions/administration.actions';
import {
  selectAllGeneralRoles,
  selectAllUsers,
} from '@main-application/administration/store/selectors/administration.selectors';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import {
  changeCustomFieldAccess,
  changeCustomFieldAccessFailure,
  changeCustomFieldAccessSuccess,
  createCustomField,
  createCustomFieldFailure,
  createCustomFieldSuccess,
  updateCustomField,
  updateCustomFieldFailure,
  updateCustomFieldSuccess,
} from '@main-application/management/store/management.actions';
import { AreaPageElementQuestionModel } from '@main-application/shared/template-editor-dialog/models/template-editor-model';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { GeneralRole } from '@shared/interfaces/general-role';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { ColorPickerModule } from '@ui-components/components/color-picker/color-picker.module';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { InputErrorModule } from '@ui-components/components/input-error/input-error.module';
import { DateDropdownModule } from '@ui-components/controls/date-dropdown/date-dropdown.module';
import { InputModule } from '@ui-components/controls/input/input.module';
import { InputAreaModule } from '@ui-components/controls/input-area/input-area.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';
import { UserSelectDropdownModule } from '@ui-components/controls/user-select-dropdown/user-select-dropdown.module';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';
import { ModalsService } from '@ui-components/modals/modals.service';

import {
  CustomDropDownOption,
  CustomFieldAccessModel,
  CustomFieldDestination,
  CustomFieldModel,
  CustomFieldType,
  CustomFieldsService,
  DropDownOptionFormGroup,
} from '../../services/custom-fields.service';

@UntilDestroy()
@Component({
  selector: 'app-custom-field-modal',
  standalone: true,
  templateUrl: './custom-field-modal.component.html',
  styleUrls: ['./custom-field-modal.component.scss'],
  imports: [
    NgIf,
    NgStyle,
    AsyncPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    RadioButtonDropdownModule,
    UserSelectDropdownModule,
    InputModule,
    InputAreaModule,
    InputErrorModule,
    DateDropdownModule,
    ColorPickerModule,
    MatProgressSpinnerModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomFieldModalComponent extends PuSubscribable implements OnInit {
  EIcon = EIcon;
  EColorPalette = EColorPalette;
  CustomFieldType = CustomFieldType;

  private store = inject(Store);
  private actions$ = inject(Actions);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private timezoneService = inject(TimezoneService);
  private customFieldsService = inject(CustomFieldsService);
  private modalsService = inject(ModalsService);
  private snackbarService = inject(SnackbarService);
  private dialogRef = inject(MatDialogRef<CustomFieldModalComponent>);

  public data = inject<CustomFieldModel>(MAT_DIALOG_DATA);
  public isSaving = false;
  users$: Observable<RestUserModel[]> = inject(Store).select(selectAllUsers);
  roles$: Observable<IRadioButtonOption<number>[]> = this.store.select(selectAllGeneralRoles).pipe(
    untilDestroyed(this),
    filter((generalRoles: GeneralRole[]) => Array.isArray(generalRoles) && generalRoles?.length > 0),
    map((generalRoles: GeneralRole[]) =>
      generalRoles.map(role => ({
        value: role.id,
        label: role.roleName,
      }))
    )
  );

  dropdownDefaultOptions: IRadioButtonOption<string | number>[] = [];
  errorDropdownRequired = false;

  private readonly today = this.timezoneService.getCurrentDateOnly();

  get dropDownOptions(): FormArray<DropDownOptionFormGroup> {
    return this.form.get('dropDownOptions') as FormArray<DropDownOptionFormGroup>;
  }

  form = this.fb.group(
    {
      id: [null],
      name: [null as string, Validators.required],
      rolesView: [null],
      rolesEdit: [null],
      appliesTo: [[CustomFieldDestination.Unit], Validators.required],
      customFieldType: [CustomFieldType.Boolean, Validators.required],
      addToSearchAndFilters: [true, Validators.required],
      defaultValueInt: [null as number, [Validators.min(-2147483647), Validators.max(2147483647)]],
      defaultValueText: [null as string],
      defaultValueNumber: [null as number, [Validators.min(-999999999.99), Validators.max(999999999.99)]],
      defaultValueDate: [this.today],
      defaultValueTime: [null],
      defaultValueUserId: [null],
      defaultValueMoney: [null],
      dropDownOptions: this.fb.array<FormGroup>([]),
      defaultValueDropDown: [null],
      defaultValueBool: [null],
    },
    { validators: this.dateTimeValidator }
  );

  private dateTimeValidator(form: FormGroup): { [key: string]: any } | null {
    const type = form.get('customFieldType')?.value;
    if (type !== CustomFieldType.DateTime) return null;

    const date = form.get('defaultValueDate')?.value;
    const time = form.get('defaultValueTime')?.value;

    const bothFilled = date && time;
    const bothEmpty = !date && !time;

    if (bothFilled || bothEmpty) {
      return null;
    }

    return { dateTimeIncomplete: true };
  }

  dateTimes: { value: Date; label: string }[];

  fieldTypeOptions: IRadioButtonOption<CustomFieldType>[] = Object.values(CustomFieldType)
    .filter(value => typeof value === 'number')
    .map(value => {
      let label = CustomFieldType[value as CustomFieldType];

      if (label === 'DropDown') {
        label = 'Dropdown';
      } else if (label === 'DateTime') {
        label = 'Date & Time';
      }

      return {
        value: value as CustomFieldType,
        label,
      };
    });

  appliesToOptions: IRadioButtonOption<CustomFieldDestination>[] = [
    { value: CustomFieldDestination.Unit, label: 'Unit' },
    { value: CustomFieldDestination.Portfolio, label: 'Portfolio' },
    { value: CustomFieldDestination.Property, label: 'Property' },
  ];

  yesNoOptions: IRadioButtonOption<boolean>[] = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];

  dropDownOptionsList: IRadioButtonOption<string>[][] = [];

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.store.dispatch(loadAllGeneralRoles());

    if (this.data) {
      const field = this.data;
      field.defaultValueDateTime ? this.setDateTimes(field.defaultValueDateTime) : this.setDateTimes(this.today);

      this.form.patchValue({
        id: field.id,
        name: field.name,
        rolesView: field.roles?.map(r => r.roleId) ?? [],
        rolesEdit: field.roles?.filter(r => r.canEdit).map(r => r.roleId) ?? [],
        appliesTo: this.fromBitmask(field.appliesToMask),
        customFieldType: field.customFieldType,
        defaultValueBool: field.defaultValueBool ?? null,
        defaultValueInt: field.defaultValueInt ?? null,
        defaultValueText: field.defaultValueText ?? null,
        defaultValueNumber: field.defaultValueNumber ?? null,
        defaultValueMoney: this.customFieldsService.formatAmount(field.defaultValueMoney) ?? null,
        defaultValueDropDown: field.defaultValueDropDown ?? null,
        defaultValueDate: field.defaultValueDate
          ? new Date(field.defaultValueDate)
          : field.defaultValueDateTime
          ? new Date(field.defaultValueDateTime)
          : null,
        defaultValueTime: field.defaultValueDateTime ? new Date(field.defaultValueDateTime) : null,
        defaultValueUserId: field.defaultValueUserId ?? null,
      });

      if (field.customFieldType === CustomFieldType.DropDown && field.dropDownValues) {
        const parsed = JSON.parse(field.dropDownValues) as CustomDropDownOption[];

        parsed.forEach(opt => {
          const group = this.fb.group({
            value: this.fb.control(opt.value ?? null),
            label: this.fb.control(opt.label ?? '', Validators.required),
            color: this.fb.control(opt.color ?? null),
            group: this.fb.control(opt.group ?? null),
          }) as DropDownOptionFormGroup;

          group.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => this.updateDropdownDefaultOptions())
            .untilDestroyed(this);

          this.dropDownOptions.push(group);
        });

        this.updateDropdownDefaultOptions();
      }

      this.form
        .get('rolesEdit')!
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe((editIds: number[]) => {
          const viewControl = this.form.get('rolesView')!;
          const currentViewIds = viewControl.value || [];

          const merged = [...new Set([...currentViewIds, ...editIds])];

          if (merged?.length !== currentViewIds?.length) {
            viewControl.setValue(merged);
            this.cdr.markForCheck();
          }
        })
        .untilDestroyed(this);
    } else {
      this.setDateTimes(this.today);
    }

    this.form
      .get('customFieldType')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(type => {
        if (type === CustomFieldType.DropDown && this.dropDownOptions?.length === 0) {
          this.addDropDownOption(3);
        }

        if (type !== CustomFieldType.Integer) {
          const ctrl = this.form.get('defaultValueInt');
          ctrl?.setErrors(null);
          ctrl?.markAsUntouched();
          ctrl?.markAsPristine();
        }

        if (type !== CustomFieldType.Decimal) {
          const ctrl = this.form.get('defaultValueNumber');
          ctrl?.setErrors(null);
          ctrl?.markAsUntouched();
          ctrl?.markAsPristine();
        }
      })
      .untilDestroyed(this);

    this.dropDownOptionsList = this.createGroupedDropdownOptions();
  }

  createGroupedDropdownOptions(): IRadioButtonOption<string>[][] {
    const groupedMap = new Map<string | null, IRadioButtonOption<string>[]>();

    this.dropDownOptions.controls.forEach(control => {
      const label = control.get('label')?.value;
      const value = control.get('value')?.value;
      const color = control.get('color')?.value;
      const group = control.get('group')?.value ?? null;

      const item: IRadioButtonOption<string> = {
        label,
        value,
        ...(color ? { color } : {}),
      };

      if (!groupedMap.has(group)) groupedMap.set(group, []);
      groupedMap.get(group)!.push(item);
    });

    const result: IRadioButtonOption<string>[][] = [];

    for (const [groupName, items] of groupedMap.entries()) {
      const clonedItems = items.map(item => ({
        ...item,
        ...(groupName ? { groupLabel: groupName } : {}),
      }));
      result.push(clonedItems);
    }

    return result;
  }

  getDropdownItemByValue(value: string): IRadioButtonOption<string> | null {
    for (const group of this.dropDownOptionsList) {
      const match = group?.find(opt => opt.value === value);
      if (match) return match;
    }
    return null;
  }

  private setDateTimes(date?: Date): void {
    this.dateTimes = this.customFieldsService.getDateTimes(date);
  }

  addDropDownOption(iterations = 1) {
    for (let i = 0; i < iterations; i++) {
      const group = this.fb.group({
        value: this.fb.control(crypto.randomUUID()),
        label: this.fb.control(`Item ${this.dropDownOptions?.length + 1}`, Validators.required),
        color: this.fb.control(null),
        group: this.fb.control(null),
      }) as DropDownOptionFormGroup;

      group.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.updateDropdownDefaultOptions();
        })
        .untilDestroyed(this);

      this.dropDownOptions.push(group);
    }

    this.updateDropdownDefaultOptions();
  }

  setDefaultDropDown(index: number) {
    const uuid = this.dropDownOptions.at(index).get('value')?.value;
    this.form.get('defaultValueDropDown')?.setValue(uuid);
    this.errorDropdownRequired = false;
  }

  removeDropDownOption(index: number) {
    this.dropDownOptions.removeAt(index);
    this.updateDropdownDefaultOptions();
  }

  private updateDropdownDefaultOptions() {
    this.dropdownDefaultOptions = this.dropDownOptions.controls.map(control => {
      const label = control.get('label')?.value;
      const value = control.get('value')?.value;
      return { label, value };
    });
  }

  save(onSaveOptions = false) {
    const id = this.form.value.id;
    const customFieldType = this.form.value.customFieldType;

    if (id && onSaveOptions && customFieldType === CustomFieldType.DropDown) {
      const name = this.form.value.name?.trim();
      const appliesToMask = this.toBitmask(this.form.value.appliesTo);
      const defaultValueDropDown = this.form.value.defaultValueDropDown;
      const dropDownValues =
        this.dropDownOptions?.length > 0
          ? JSON.stringify(
              this.dropDownOptions.getRawValue().map(opt => {
                const result: any = {
                  label: opt.label,
                  value: opt.value?.startsWith('#') ? crypto.randomUUID() : opt.value,
                };
                if (opt.color) result.color = opt.color;
                if (opt.group) result.group = opt.group;
                return result;
              })
            )
          : undefined;

      const fieldData = { id, name, appliesToMask, defaultValueDropDown, dropDownValues };
      this.store.dispatch(updateCustomField({ field: fieldData }));
      return;
    }

    if (customFieldType === CustomFieldType.DateTime) {
      const date = this.form.value.defaultValueDate;
      const time = this.form.value.defaultValueTime;
      const bothFilled = !!date && !!time;
      const bothEmpty = !date && !time;

      if (!(bothFilled || bothEmpty)) {
        const missing = !date ? 'date' : 'time';
        this.snackbarService.error(`Please select default ${missing}`);
        this.form.markAllAsTouched();
        this.cdr.markForCheck();
        return;
      }
    }

    if (this.form.valid) {
      this.isSaving = true;
      const date = this.form.value.defaultValueDate;
      const time = this.form.value.defaultValueTime;

      let defaultValueDateTime: Date | undefined = undefined;
      if (customFieldType === CustomFieldType.DateTime && date && time) {
        const combined = new Date(date);
        const timeObj = new Date(time);
        combined.setHours(timeObj.getHours());
        combined.setMinutes(timeObj.getMinutes());
        defaultValueDateTime = combined;
      }

      const rawMoney = this.form.value.defaultValueMoney;
      const parsedMoney =
        customFieldType === CustomFieldType.Amount ? this.customFieldsService.parseAmount(rawMoney) : undefined;

      const fieldData: CustomFieldModel = {
        id: this.form.value.id,
        name: this.form.value.name.trim(),
        customFieldType: this.form.value.id === null ? customFieldType : undefined,
        appliesToMask: this.toBitmask(this.form.value.appliesTo),
        addToSearchAndFilters: this.form.value.addToSearchAndFilters || undefined,
        defaultValueInt:
          customFieldType === CustomFieldType.Integer && this.form.value.defaultValueInt
            ? Number(this.form.value.defaultValueInt)
            : undefined,
        defaultValueText:
          customFieldType === CustomFieldType.Text && this.form.value.defaultValueText
            ? String(this.form.value.defaultValueText)
            : undefined,
        defaultValueNumber:
          customFieldType === CustomFieldType.Decimal && this.form.value.defaultValueNumber
            ? Number(this.form.value.defaultValueNumber)
            : undefined,
        defaultValueDate: customFieldType === CustomFieldType.Date ? this.form.value.defaultValueDate : undefined,
        defaultValueDateTime,
        defaultValueUserId: customFieldType === CustomFieldType.User ? this.form.value.defaultValueUserId : undefined,
        defaultValueMoney: parsedMoney,
        defaultValueDropDown:
          customFieldType === CustomFieldType.DropDown ? this.form.value.defaultValueDropDown : undefined,
        defaultValueBool: customFieldType === CustomFieldType.Boolean ? this.form.value.defaultValueBool : undefined,
        dropDownValues:
          this.dropDownOptions?.length > 0
            ? JSON.stringify(
                this.dropDownOptions.getRawValue().map(opt => {
                  const result: any = {
                    label: opt.label,
                    value: opt.value?.startsWith('#') ? crypto.randomUUID() : opt.value,
                  };
                  if (opt.color) result.color = opt.color;
                  if (opt.group) result.group = opt.group;
                  return result;
                })
              )
            : undefined,
      };

      const isNew = fieldData.id === null;
      this.store.dispatch(isNew ? createCustomField({ field: fieldData }) : updateCustomField({ field: fieldData }));

      this.actions$
        .pipe(
          ofType(
            isNew ? createCustomFieldSuccess : updateCustomFieldSuccess,
            isNew ? createCustomFieldFailure : updateCustomFieldFailure
          ),
          take(1)
        )
        .subscribe(action => {
          if (action.type === createCustomFieldSuccess.type || action.type === updateCustomFieldSuccess.type) {
            const fieldId = isNew
              ? (action as ReturnType<typeof createCustomFieldSuccess>).field.id
              : this.form.value.id;

            const allIds = Array.from(
              new Set([...(this.form.value.rolesView || []), ...(this.form.value.rolesEdit || [])])
            );

            const roles: CustomFieldAccessModel[] = allIds.map(roleId => ({
              roleId,
              canEdit: (this.form.value.rolesEdit || []).includes(roleId),
            }));

            if (roles?.length && fieldId) {
              this.store.dispatch(
                changeCustomFieldAccess({
                  payload: {
                    customFieldId: fieldId,
                    roles,
                  },
                })
              );

              this.actions$
                .pipe(
                  ofType(changeCustomFieldAccessSuccess, changeCustomFieldAccessFailure),
                  take(1),
                  untilDestroyed(this)
                )
                .subscribe(accessAction => {
                  this.isSaving = false;
                  if (accessAction.type === changeCustomFieldAccessSuccess.type) {
                    this.dialogRef.close({ result: DialogResult.Success });
                  }
                  this.cdr.markForCheck();
                })
                .untilDestroyed(this);
            } else {
              this.isSaving = false;
              this.dialogRef.close({ result: DialogResult.Success });
              this.cdr.markForCheck();
            }
          } else {
            this.isSaving = false;
            this.cdr.markForCheck();
          }
        })
        .untilDestroyed(this);
    } else {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
    }
  }

  cancel() {
    this.dialogRef.close({ result: DialogResult.Fail, data: null });
  }

  private toBitmask(selected: CustomFieldDestination[]): number {
    return selected.reduce((acc, value) => acc | value, 0);
  }

  private fromBitmask(mask: number): CustomFieldDestination[] {
    return Object.values(CustomFieldDestination)
      .filter(v => typeof v === 'number')
      .filter(v => (mask & (v as number)) !== 0) as CustomFieldDestination[];
  }

  public openDropdownOptionsEditor(optionControls: DropDownOptionFormGroup[]): void {
    const options = optionControls.map(group => {
      const raw = group.getRawValue();

      return {
        label: raw.label,
        value: raw.value,
        color: raw.color,
        group: raw.group ?? null,
      };
    });

    const model: AreaPageElementQuestionModel = {
      type: 'dropdown',
      guid: crypto.randomUUID(),
      name:
        this.form.get('name')?.value && this.form.get('name')?.value !== '' ? this.form.get('name')?.value : 'Dropdown',
      title:
        this.form.get('name')?.value && this.form.get('name')?.value !== '' ? this.form.get('name')?.value : 'Dropdown',
      options,
      defaultValue: this.form.get('defaultValueDropDown')?.value,
    };

    this.modalsService
      .openDropdownOptionsEditor(model)
      .afterClosed()
      .pipe(take(1), untilDestroyed(this))
      .subscribe(result => {
        if (result?.result === DialogResult.Success && result.data) {
          this.form.get('name')?.setValue(result.data?.title ?? '');
          const id = this.form.value.id;
          const updatedOptions = result.data.options ?? [];

          this.dropDownOptions.clear();

          updatedOptions.forEach(opt => {
            const group = this.fb.group({
              value: this.fb.control(opt.value ?? undefined),
              label: this.fb.control(opt.label ?? '', Validators.required),
              color: this.fb.control(opt.color ?? undefined),
              group: this.fb.control(opt.group ?? undefined),
            }) as DropDownOptionFormGroup;

            group.valueChanges
              .pipe(untilDestroyed(this))
              .subscribe(() => this.updateDropdownDefaultOptions())
              .untilDestroyed(this);

            this.dropDownOptions.push(group);
          });

          this.form.get('defaultValueDropDown')?.setValue(result.data.defaultValue);
          this.updateDropdownDefaultOptions();
          this.cdr.markForCheck();

          this.dropDownOptionsList = this.createGroupedDropdownOptions();

          if (id) this.save(true);
        }
      })
      .untilDestroyed(this);
  }
}
